<script setup lang="ts">
/**
 * An entry that opens a second menu beside it — one level deep („Tags ▸“).
 * Opens on hover, click, Enter, Space or ArrowRight; ArrowLeft or Escape
 * closes it and returns to the entry. Its items close the whole menu as
 * usual. The default slot holds the submenu's items.
 *
 * The slot may also hold content that is not menu items (a colour palette):
 * opening by keyboard then focuses its first focusable element, ArrowUp/Down,
 * Home and End walk its focusables unless the content uses the key itself, and
 * ArrowLeft returns to the entry only when the content left it alone. A click
 * on an entry the pointer just opened by hovering keeps it open.
 */
import ChevronRight from 'lucide-vue-next/dist/esm/icons/chevron-right.js';
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useFloatingPanel } from '../Popover';
import { menuItemsOf, moveMenuFocus, setTopLayer } from './menuContext';

const props = withDefaults(
  defineProps<{
    label: string;
    disabled?: boolean;
  }>(),
  { disabled: false }
);

const open = ref(false);
const row = ref<HTMLElement | null>(null);
const panel = ref<HTMLElement | null>(null);
const { floatingStyles } = useFloatingPanel(row, panel, {
  open,
  placement: 'right-start',
  offset: 4,
});

let hoverTimer: ReturnType<typeof setTimeout> | undefined;
/** Opened by the hover delay: the click that usually follows keeps it open. */
let openedByHover = false;

const FOCUSABLE =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Focusable elements of non-item content, in document order. */
function focusablesOf(el: HTMLElement | null): HTMLElement[] {
  if (!el) return [];
  return Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (node) => node.getAttribute('aria-disabled') !== 'true'
  );
}

function focusFirst() {
  const target = menuItemsOf(panel.value)[0] ?? focusablesOf(panel.value)[0];
  target?.focus({ preventScroll: true });
}

/** Walks non-item content like a menu: ArrowUp/Down (wrapping), Home, End. */
function moveContentFocus(key: string): boolean {
  if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(key)) return false;
  const all = focusablesOf(panel.value);
  if (!all.length) return true;
  const current = all.indexOf(document.activeElement as HTMLElement);
  let next: number;
  if (key === 'Home') next = 0;
  else if (key === 'End') next = all.length - 1;
  else if (current === -1) next = key === 'ArrowDown' ? 0 : all.length - 1;
  else next = (current + (key === 'ArrowDown' ? 1 : -1) + all.length) % all.length;
  all[next]?.focus();
  return true;
}

function isTextField(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  if (el.isContentEditable || el.tagName === 'TEXTAREA') return true;
  return (
    el.tagName === 'INPUT' &&
    !['button', 'checkbox', 'color', 'radio', 'range', 'submit', 'reset'].includes(
      (el as HTMLInputElement).type
    )
  );
}

watch(open, async (isOpen) => {
  await nextTick();
  setTopLayer(panel.value, isOpen);
});

function show(focusContent: boolean) {
  if (props.disabled) return;
  openedByHover = false;
  open.value = true;
  if (focusContent) void nextTick(focusFirst);
}
function hide(focusRow: boolean) {
  openedByHover = false;
  open.value = false;
  if (focusRow) row.value?.focus({ preventScroll: true });
}

function onEnter() {
  clearTimeout(hoverTimer);
  hoverTimer = setTimeout(() => {
    if (open.value) return;
    show(false);
    openedByHover = true;
  }, 80);
}
function onLeave() {
  clearTimeout(hoverTimer);
  hoverTimer = setTimeout(() => (open.value = false), 160);
}

function onRowClick() {
  clearTimeout(hoverTimer);
  if (!open.value) {
    show(true);
  } else if (openedByHover) {
    // The pointer opened it a moment ago; this click confirms, it does not toggle.
    openedByHover = false;
    void nextTick(focusFirst);
  } else {
    hide(false);
  }
}

function onRowKeydown(event: KeyboardEvent) {
  if (['ArrowRight', 'Enter', ' '].includes(event.key)) {
    event.preventDefault();
    event.stopPropagation();
    show(true);
  }
}

function onPanelKeydown(event: KeyboardEvent) {
  const hasItems = menuItemsOf(panel.value).length > 0;
  // Content that used the key itself (a palette's grid, a text field's caret) keeps it.
  const contentOwns = !hasItems && (event.defaultPrevented || isTextField(event.target));
  if (event.key === 'Escape' || (event.key === 'ArrowLeft' && !contentOwns)) {
    event.preventDefault();
    event.stopPropagation();
    hide(true);
    return;
  }
  if (!hasItems) {
    if (contentOwns) {
      if (['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key))
        event.stopPropagation();
      return;
    }
    if (moveContentFocus(event.key)) {
      event.preventDefault();
      event.stopPropagation();
    }
    return;
  }
  if (moveMenuFocus(panel.value, event.key)) {
    event.preventDefault();
    event.stopPropagation();
  }
}

onBeforeUnmount(() => clearTimeout(hoverTimer));
</script>

<template>
  <div class="nx-context-submenu" @mouseenter="onEnter" @mouseleave="onLeave">
    <button
      ref="row"
      type="button"
      class="nx-context-submenu__row"
      role="menuitem"
      tabindex="-1"
      aria-haspopup="menu"
      :aria-expanded="open"
      :aria-disabled="props.disabled || undefined"
      :disabled="props.disabled"
      @click="onRowClick"
      @keydown="onRowKeydown"
    >
      <span v-if="$slots.icon" class="nx-context-submenu__icon" aria-hidden="true">
        <slot name="icon" />
      </span>
      <span class="nx-context-submenu__label">{{ props.label }}</span>
      <ChevronRight class="nx-context-submenu__chevron nxt-icon-sm" aria-hidden="true" />
    </button>
    <div
      v-if="open"
      ref="panel"
      class="nx-context-submenu__panel"
      popover="manual"
      role="menu"
      :aria-label="props.label"
      :style="floatingStyles"
      @keydown="onPanelKeydown"
    >
      <slot />
    </div>
  </div>
</template>

<style scoped>
.nx-context-submenu {
  position: relative;
}
.nx-context-submenu__row {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-sm);
  width: 100%;
  min-height: var(--nxt-control-size-sm);
  padding: var(--nxt-space-2xs) var(--nxt-space-sm);
  border: none;
  border-radius: var(--nxt-radius-md);
  background: transparent;
  color: var(--nxt-menu-item-fg, var(--nxt-fg));
  font: inherit;
  font-size: var(--nxt-menu-item-size, var(--nxt-font-size-sm));
  text-align: start;
  cursor: pointer;
  outline: none;
}
.nx-context-submenu__row:hover:not(:disabled),
.nx-context-submenu__row:focus-visible,
.nx-context-submenu__row[aria-expanded='true'] {
  background: var(--nxt-menu-item-bg-hover);
}
.nx-context-submenu__row:focus-visible {
  box-shadow: inset 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring);
}
.nx-context-submenu__row:disabled {
  cursor: not-allowed;
  opacity: 0.6;
}
.nx-context-submenu__icon {
  display: inline-flex;
  flex: 0 0 auto;
  justify-content: center;
  width: calc(var(--nxt-size-unit) * 1.125);
  color: var(--nxt-fg-muted);
}
.nx-context-submenu__label {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.nx-context-submenu__chevron {
  flex: 0 0 auto;
  color: var(--nxt-fg-muted);
}
.nx-context-submenu__panel {
  position: fixed;
  display: flex;
  flex-direction: column;
  gap: calc(var(--nxt-hairline) * 2);
  min-width: calc(var(--nxt-size-unit) * 13);
  max-height: min(calc(var(--nxt-size-unit) * 30), calc(100vh - var(--nxt-size-unit)));
  margin: 0;
  padding: var(--nxt-space-2xs);
  overflow-y: auto;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  background: var(--nxt-surface-raised-bg);
  box-shadow: var(--nxt-shadow-lg);
}
</style>
